import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { api, useAuth } from "../App";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { 
  Building2, 
  Calendar, 
  GraduationCap, 
  ClipboardCheck, 
  MapPin,
  TrendingUp,
  Clock,
  ArrowRight,
  Users,
  Activity,
  PieChart as PieChartIcon
} from "lucide-react";
import { toast } from "sonner";
import {
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer
} from "recharts";

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [recentPlacements, setRecentPlacements] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [statsRes, analyticsRes, placementsRes] = await Promise.all([
        api.get("/dashboard/stats"),
        api.get("/dashboard/analytics"),
        api.get("/placements")
      ]);
      setStats(statsRes.data);
      setAnalytics(analyticsRes.data);
      setRecentPlacements(placementsRes.data.slice(0, 5));
    } catch (error) {
      toast.error("Failed to load dashboard data");
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: "status-pending",
      accepted: "status-accepted",
      confirmed: "status-confirmed",
      rejected: "status-rejected",
    };
    return <Badge className={styles[status]}>{status}</Badge>;
  };

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0];
      return (
        <div className="bg-slate-800 border border-slate-600 rounded-lg px-3 py-2 shadow-lg">
          <p className="text-slate-200 text-sm font-medium">
            {data.payload.stage || data.payload.name || data.payload.program || data.payload.specialty}
          </p>
          <p className="text-cyan-400 text-sm">{data.value}</p>
        </div>
      );
    }
    return null;
  };

  const getUserDisplayName = () => {
    if (user?.first_name) return user.first_name;
    if (user?.name) return user.name.split(" ")[0];
    return "there";
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <div className="spinner w-12 h-12"></div>
      </div>
    );
  }

  const pipelineData = analytics?.placement_pipeline || [];
  const rotationData = analytics?.rotation_availability?.filter(d => d.value > 0) || [];
  const programData = analytics?.students_by_program || [];
  const specialtyData = analytics?.sites_by_specialty || [];

  return (
    <div className="space-y-6 sm:space-y-8 animate-fade-in" data-testid="dashboard">
      {/* Header */}
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold text-slate-100">
          Welcome back, {getUserDisplayName()}
        </h1>
        <p className="text-slate-400 mt-1 text-sm sm:text-base">
          Here's what's happening with your clinical placements
        </p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        <Card 
          className="bg-[#151A23] border-slate-700 hover:border-cyan-400/50 transition-colors cursor-pointer"
          onClick={() => navigate("/map")}
          data-testid="stat-clinical-sites"
        >
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-400 text-sm">Clinical Sites</p>
                <p className="text-3xl font-bold text-slate-100 mt-1">{stats?.clinical_sites || 0}</p>
              </div>
              <div className="w-12 h-12 bg-cyan-400/10 rounded-xl flex items-center justify-center">
                <Building2 className="w-6 h-6 text-cyan-400" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card 
          className="bg-[#151A23] border-slate-700 hover:border-cyan-400/50 transition-colors cursor-pointer"
          onClick={() => navigate("/rotations")}
          data-testid="stat-available-slots"
        >
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-400 text-sm">Open Rotations</p>
                <p className="text-3xl font-bold text-slate-100 mt-1">{stats?.available_slots || 0}</p>
                <p className="text-xs text-slate-500 mt-1">of {stats?.total_slots || 0} total</p>
              </div>
              <div className="w-12 h-12 bg-emerald-400/10 rounded-xl flex items-center justify-center">
                <Calendar className="w-6 h-6 text-emerald-400" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card 
          className="bg-[#151A23] border-slate-700 hover:border-cyan-400/50 transition-colors cursor-pointer"
          onClick={() => navigate("/students")}
          data-testid="stat-students"
        >
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-400 text-sm">Students</p>
                <p className="text-3xl font-bold text-slate-100 mt-1">{stats?.students || 0}</p>
              </div>
              <div className="w-12 h-12 bg-purple-400/10 rounded-xl flex items-center justify-center">
                <GraduationCap className="w-6 h-6 text-purple-400" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card 
          className="bg-[#151A23] border-slate-700 hover:border-cyan-400/50 transition-colors cursor-pointer"
          onClick={() => navigate("/approvals")}
          data-testid="stat-pending"
        >
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-400 text-sm">Pending Approvals</p>
                <p className="text-3xl font-bold text-slate-100 mt-1">{stats?.placements?.pending || 0}</p>
              </div>
              <div className="w-12 h-12 bg-yellow-400/10 rounded-xl flex items-center justify-center">
                <Clock className="w-6 h-6 text-yellow-400" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Placement Pipeline - Horizontal Bar Chart (Funnel style) */}
        <Card className="bg-[#151A23] border-slate-700" data-testid="placement-pipeline-chart">
          <CardHeader>
            <CardTitle className="text-slate-100 flex items-center gap-2">
              <Activity className="w-5 h-5 text-cyan-400" />
              Placement Pipeline
            </CardTitle>
            <p className="text-slate-400 text-sm">Request journey from submission to confirmation</p>
          </CardHeader>
          <CardContent>
            {pipelineData.some(item => item.value > 0) ? (
              <ResponsiveContainer width="100%" height={280}>
                <BarChart
                  data={pipelineData}
                  layout="vertical"
                  margin={{ top: 5, right: 30, left: 80, bottom: 5 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#334155" horizontal={false} />
                  <XAxis type="number" stroke="#94a3b8" fontSize={12} />
                  <YAxis 
                    dataKey="stage" 
                    type="category" 
                    stroke="#94a3b8" 
                    fontSize={12}
                    width={75}
                  />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="value" radius={[0, 4, 4, 0]}>
                    {pipelineData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.fill} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex flex-col items-center justify-center h-[280px] text-slate-500">
                <Activity className="w-12 h-12 mb-3 opacity-50" />
                <p>No placement requests yet</p>
                <p className="text-sm">Submit requests to see the pipeline</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Rotation Availability - Donut Chart */}
        <Card className="bg-[#151A23] border-slate-700" data-testid="rotation-availability-chart">
          <CardHeader>
            <CardTitle className="text-slate-100 flex items-center gap-2">
              <PieChartIcon className="w-5 h-5 text-emerald-400" />
              Rotation Availability
            </CardTitle>
            <p className="text-slate-400 text-sm">Current capacity utilization across all rotations</p>
          </CardHeader>
          <CardContent>
            {analytics?.summary?.total_capacity > 0 ? (
              <div className="flex items-center">
                <ResponsiveContainer width="60%" height={280}>
                  <PieChart>
                    <Pie
                      data={rotationData}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={100}
                      paddingAngle={2}
                      dataKey="value"
                    >
                      {rotationData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.fill} />
                      ))}
                    </Pie>
                    <Tooltip content={<CustomTooltip />} />
                  </PieChart>
                </ResponsiveContainer>
                <div className="w-[40%] space-y-3">
                  {(analytics?.rotation_availability || []).map((item, index) => (
                    <div key={index} className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div 
                          className="w-3 h-3 rounded-full" 
                          style={{ backgroundColor: item.fill }}
                        />
                        <span className="text-slate-300 text-sm">{item.name}</span>
                      </div>
                      <span className="text-slate-100 font-semibold">{item.value}</span>
                    </div>
                  ))}
                  <div className="pt-2 border-t border-slate-700">
                    <div className="flex items-center justify-between">
                      <span className="text-slate-400 text-sm">Utilization</span>
                      <span className="text-cyan-400 font-bold">{analytics?.summary?.utilization_rate || 0}%</span>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center h-[280px] text-slate-500">
                <Calendar className="w-12 h-12 mb-3 opacity-50" />
                <p>No rotations created yet</p>
                <p className="text-sm">Create rotations to see availability</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Second Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Students by Program */}
        <Card className="bg-[#151A23] border-slate-700" data-testid="students-by-program-chart">
          <CardHeader>
            <CardTitle className="text-slate-100 flex items-center gap-2">
              <Users className="w-5 h-5 text-purple-400" />
              Students by Program
            </CardTitle>
          </CardHeader>
          <CardContent>
            {programData.length > 0 ? (
              <ResponsiveContainer width="100%" height={250}>
                <BarChart 
                  data={programData} 
                  layout="vertical"
                  margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#334155" horizontal={false} />
                  <XAxis type="number" stroke="#94a3b8" fontSize={12} />
                  <YAxis 
                    dataKey="program" 
                    type="category" 
                    stroke="#94a3b8" 
                    fontSize={11}
                    width={100}
                    tickFormatter={(value) => value.length > 15 ? value.substring(0, 15) + "..." : value}
                  />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="count" fill="#a855f7" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex flex-col items-center justify-center h-[250px] text-slate-500">
                <GraduationCap className="w-12 h-12 mb-3 opacity-50" />
                <p>No students registered yet</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Sites by Specialty */}
        <Card className="bg-[#151A23] border-slate-700" data-testid="sites-by-specialty-chart">
          <CardHeader>
            <CardTitle className="text-slate-100 flex items-center gap-2">
              <Building2 className="w-5 h-5 text-cyan-400" />
              Sites by Specialty
            </CardTitle>
          </CardHeader>
          <CardContent>
            {specialtyData.length > 0 ? (
              <div className="flex items-center">
                <ResponsiveContainer width="55%" height={250}>
                  <PieChart>
                    <Pie
                      data={specialtyData}
                      cx="50%"
                      cy="50%"
                      outerRadius={85}
                      dataKey="count"
                      label={({ percent }) => (percent * 100).toFixed(0) + "%"}
                      labelLine={false}
                    >
                      {specialtyData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.fill} />
                      ))}
                    </Pie>
                    <Tooltip content={<CustomTooltip />} />
                  </PieChart>
                </ResponsiveContainer>
                <div className="w-[45%] space-y-2">
                  {specialtyData.map((item, index) => (
                    <div key={index} className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div 
                          className="w-2.5 h-2.5 rounded-full" 
                          style={{ backgroundColor: item.fill }}
                        />
                        <span className="text-slate-300 text-xs truncate max-w-[100px]" title={item.specialty}>
                          {item.specialty}
                        </span>
                      </div>
                      <span className="text-slate-100 text-sm font-medium">{item.count}</span>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center h-[250px] text-slate-500">
                <Building2 className="w-12 h-12 mb-3 opacity-50" />
                <p>No clinical sites yet</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Placement Status Overview */}
        <Card className="lg:col-span-1 bg-[#151A23] border-slate-700" data-testid="placement-overview">
          <CardHeader>
            <CardTitle className="text-slate-100 flex items-center gap-2">
              <ClipboardCheck className="w-5 h-5 text-cyan-400" />
              Placement Status
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between p-3 bg-slate-800/50 rounded-lg">
              <div className="flex items-center gap-3">
                <div className="w-3 h-3 bg-yellow-500 rounded-full" />
                <span className="text-slate-300">Pending</span>
              </div>
              <span className="text-slate-100 font-semibold">{stats?.placements?.pending || 0}</span>
            </div>
            <div className="flex items-center justify-between p-3 bg-slate-800/50 rounded-lg">
              <div className="flex items-center gap-3">
                <div className="w-3 h-3 bg-green-500 rounded-full" />
                <span className="text-slate-300">Accepted</span>
              </div>
              <span className="text-slate-100 font-semibold">{stats?.placements?.accepted || 0}</span>
            </div>
            <div className="flex items-center justify-between p-3 bg-slate-800/50 rounded-lg">
              <div className="flex items-center gap-3">
                <div className="w-3 h-3 bg-blue-500 rounded-full" />
                <span className="text-slate-300">Confirmed</span>
              </div>
              <span className="text-slate-100 font-semibold">{stats?.placements?.confirmed || 0}</span>
            </div>
            <div className="flex items-center justify-between p-3 bg-slate-800/50 rounded-lg">
              <div className="flex items-center gap-3">
                <div className="w-3 h-3 bg-red-500 rounded-full" />
                <span className="text-slate-300">Rejected</span>
              </div>
              <span className="text-slate-100 font-semibold">{stats?.placements?.rejected || 0}</span>
            </div>
          </CardContent>
        </Card>

        {/* Recent Placements */}
        <Card className="lg:col-span-2 bg-[#151A23] border-slate-700" data-testid="recent-placements">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-slate-100 flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-cyan-400" />
              Recent Placements
            </CardTitle>
            <Button 
              variant="ghost" 
              size="sm" 
              className="text-cyan-400 hover:text-cyan-300"
              onClick={() => navigate("/approvals")}
              data-testid="view-all-placements-btn"
            >
              View All <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          </CardHeader>
          <CardContent>
            {recentPlacements.length === 0 ? (
              <div className="text-center py-8">
                <ClipboardCheck className="w-12 h-12 text-slate-600 mx-auto mb-3" />
                <p className="text-slate-400">No placements yet</p>
                <p className="text-slate-500 text-sm mt-1">Create slots and assign students to get started</p>
              </div>
            ) : (
              <div className="space-y-3">
                {recentPlacements.map((placement) => (
                  <div 
                    key={placement.id} 
                    className="flex items-center justify-between p-4 bg-slate-800/30 rounded-lg hover:bg-slate-800/50 transition-colors"
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 bg-cyan-400/10 rounded-lg flex items-center justify-center">
                        <GraduationCap className="w-5 h-5 text-cyan-400" />
                      </div>
                      <div>
                        <p className="text-slate-200 font-medium">{placement.student_name}</p>
                        <p className="text-slate-500 text-sm">{placement.slot_title} - {placement.clinical_site_name}</p>
                      </div>
                    </div>
                    {getStatusBadge(placement.status)}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Quick Actions */}
      <Card className="bg-[#151A23] border-slate-700" data-testid="quick-actions">
        <CardHeader>
          <CardTitle className="text-slate-100">Quick Actions</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {(user?.role === "admin" || user?.role === "clinical_site") && (
              <Button 
                className="h-auto py-4 bg-slate-800 hover:bg-slate-700 text-left justify-start"
                onClick={() => navigate("/rotations")}
                data-testid="quick-action-create-slot"
              >
                <Calendar className="w-5 h-5 mr-3 text-cyan-400" />
                <div>
                  <p className="font-semibold text-slate-200">Create Rotation</p>
                  <p className="text-xs text-slate-400">Add new clinical rotation</p>
                </div>
              </Button>
            )}
            {(user?.role === "admin" || user?.role === "university") && (
              <Button 
                className="h-auto py-4 bg-slate-800 hover:bg-slate-700 text-left justify-start"
                onClick={() => navigate("/students")}
                data-testid="quick-action-add-student"
              >
                <GraduationCap className="w-5 h-5 mr-3 text-purple-400" />
                <div>
                  <p className="font-semibold text-slate-200">Add Student</p>
                  <p className="text-xs text-slate-400">Register new students</p>
                </div>
              </Button>
            )}
            <Button 
              className="h-auto py-4 bg-slate-800 hover:bg-slate-700 text-left justify-start"
              onClick={() => navigate("/map")}
              data-testid="quick-action-view-map"
            >
              <MapPin className="w-5 h-5 mr-3 text-emerald-400" />
              <div>
                <p className="font-semibold text-slate-200">View Map</p>
                <p className="text-xs text-slate-400">Explore clinical sites</p>
              </div>
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
